import React, { useState } from 'react';
import { toast } from 'sonner';
import { CalendarOff, Plus, Trash2, Loader2 } from 'lucide-react';
import { notesApi } from '@/lib/notesApi';
import { extractError } from '@/lib/errors';

const INPUT = 'h-9 rounded-lg border border-input bg-background px-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]';
const fmt = (d) => new Date(`${d}T00:00:00`).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

function HolidayRow({ h, today, onRemove, busy }) {
  const past = (h.end || h.start) < today;
  const now = h.start <= today && today <= (h.end || h.start);
  return (
    <li className={`flex items-center gap-3 border-b border-border py-2.5 last:border-0 ${past ? 'opacity-50' : ''}`} data-testid={`tagws-holiday-${h.id}`}>
      <CalendarOff size={15} className={now ? 'text-red-600' : 'text-muted-foreground'} />
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-foreground">{h.name || 'Holiday'}{now ? <span className="ml-2 rounded bg-red-600/10 px-1.5 py-0.5 text-[length:calc(var(--fs,1)*0.625rem)] font-semibold text-red-600">Today</span> : null}{past ? <span className="ml-2 text-[length:calc(var(--fs,1)*0.625rem)] text-muted-foreground">Past</span> : null}</div>
        <div className="text-xs text-muted-foreground">{fmt(h.start)}{h.end && h.end !== h.start ? ` – ${fmt(h.end)}` : ''}</div>
      </div>
      <button type="button" onClick={onRemove} disabled={busy} aria-label="Remove holiday" className="grid h-8 w-8 place-items-center rounded-lg text-muted-foreground hover:bg-red-600/10 hover:text-red-600" data-testid={`tagws-holiday-remove-${h.id}`}><Trash2 size={15} /></button>
    </li>
  );
}

// Dates (or ranges) when the portal stays locked all day, IST — even with weekday hours off.
export function TagPortalHolidays({ h, onSaved }) {
  const [name, setName] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [busy, setBusy] = useState(false);
  const save = async (list, msg) => {
    setBusy(true);
    try {
      const { data } = await notesApi.put('/notes/tag-workspace/portal-holidays', { holidays: list.map(({ name: n, start: s, end: e }) => ({ name: n, start: s, end: e })) });
      onSaved(data);
      toast.success(msg);
      window.dispatchEvent(new Event('tagws-locked'));
      return true;
    } catch (e) { toast.error(extractError(e, 'Could not save the holidays.')); return false; } finally { setBusy(false); }
  };
  const add = async (e) => {
    e.preventDefault();
    if (!start) return toast.error('Pick a date.');
    if (end && end < start) return toast.error('The end date is before the start date.');
    if (await save([...h.holidays, { name, start, end: end || start }], 'Holiday added.')) { setName(''); setStart(''); setEnd(''); }
  };
  return (
    <div className="rounded-xl border border-border p-4" data-testid="tagws-portal-holidays">
      <div className="flex items-center gap-2 text-sm font-semibold text-foreground"><CalendarOff size={15} /> Holidays</div>
      <p className="mt-1 text-xs text-muted-foreground">The portal stays locked all day on these dates (India time), even if the weekday hours are off. Leave "To" empty for a single day.</p>
      <form onSubmit={add} className="mt-3 flex flex-wrap items-end gap-2">
        <input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} placeholder="Name, e.g. Diwali" className={`${INPUT} min-w-[10rem] flex-1`} data-testid="tagws-holiday-name" />
        <label className="text-[length:calc(var(--fs,1)*0.6875rem)] text-muted-foreground">From<input type="date" value={start} min={h.today} onChange={(e) => setStart(e.target.value)} className={`${INPUT} mt-0.5 block`} data-testid="tagws-holiday-start" /></label>
        <label className="text-[length:calc(var(--fs,1)*0.6875rem)] text-muted-foreground">To<input type="date" value={end} min={start || h.today} onChange={(e) => setEnd(e.target.value)} className={`${INPUT} mt-0.5 block`} data-testid="tagws-holiday-end" /></label>
        <button type="submit" disabled={busy || !start} className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-primary px-3 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-60" data-testid="tagws-holiday-add">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Add
        </button>
      </form>
      {h.holidays.length ? (
        <ul className="mt-3">
          {h.holidays.map((x) => <HolidayRow key={x.id} h={x} today={h.today} busy={busy} onRemove={() => save(h.holidays.filter((y) => y.id !== x.id), 'Holiday removed.')} />)}
        </ul>
      ) : <p className="mt-3 text-xs text-muted-foreground" data-testid="tagws-holidays-empty">No holidays added yet.</p>}
    </div>
  );
}
